import { IconDownload, IconFilter, IconUserOff } from '@tabler/icons-react'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DateRange } from 'react-day-picker'
import { useTranslation } from 'react-i18next'

import type {
  ScalrTableColumn,
  ScalrTableData,
} from '@/components/customComponents'
import {
  BasicDatePicker,
  BasicTable,
  Loading,
  ScalrBreadcrumb,
  ScalrEmptyBox,
  TreeSelector,
  TreeSelectorChild,
} from '@/components/customComponents'
import ScalrBadge, { semanticStyles } from '@/components/ScalrBadge'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'

import { toast, useKpiEligibleUsers, useKpiUserGoalAssignments } from '@/hooks'
import { TranslationFunctionType } from '@/i18n'
import PayoutSummary from '@/screens/targets/kpiGoalPlan/PayoutSummary'
import { formatValueForKpi } from '@/utils'
import { DateType } from '@/utils/date'

const getKpiPayoutColumns = (
  t: TranslationFunctionType
): ScalrTableColumn[] => [
  {
    accessorKey: 'userFullName',
    header: t('targets.table.user'),
    type: 'text',
  },
  {
    accessorKey: 'kpi',
    header: t('targets.table.kpi'),
    type: 'text',
  },
  {
    accessorKey: 'branchName',
    header: t('targets.table.branch'),
    type: 'text',
  },
  {
    accessorKey: 'period',
    header: t('targets.table.period'),
    type: 'text',
  },
  {
    accessorKey: 'targetValue',
    header: t('targets.table.target'),
    type: 'text',
  },
  {
    accessorKey: 'actualValue',
    header: 'Actual',
    type: 'text',
  },
  {
    accessorKey: 'progressPercent',
    header: 'Progress %',
    type: 'percentage',
  },
  {
    accessorKey: 'achieved',
    header: 'Achieved',
    type: 'text',
  },
  {
    accessorKey: 'bonusAmount',
    header: t('targets.table.bonus'),
    type: 'percentage',
  },
]

type FilterSelections = {
  dateRange?: DateRange
  dateFilterType?: DateType
}

const KPIPayoutPage = () => {
  const { t } = useTranslation()
  const [filterSelections, setFilterSelections] = useState<FilterSelections>({
    dateRange: undefined,
    dateFilterType: 'quarterly',
  })
  const [selectedEmployeeId, setSelectedEmployeeId] = useState<string>()

  const {
    eligibleUsers,
    loading: eligibleUsersLoading,
    error: eligibleUsersError,
    fetchEligibleUsers,
  } = useKpiEligibleUsers()

  const {
    fetchUserAssignments,
    userAssignments,
    loading,
    error: userError,
  } = useKpiUserGoalAssignments()

  // Fetch eligible users on mount
  useEffect(() => {
    fetchEligibleUsers()
  }, [fetchEligibleUsers])

  // Show error toast if there is an error
  useEffect(() => {
    if (userError) {
      toast({
        title: t('common.errors.serverError'),
        description: userError.message,
        variant: 'destructive',
      })
    }
    if (eligibleUsersError) {
      toast({
        title: t('common.errors.serverError'),
        description: eligibleUsersError.message,
        variant: 'destructive',
      })
    }
  }, [userError, eligibleUsersError, t])

  // Fetch KPI assignments when employee and date selection changes
  useEffect(() => {
    if (selectedEmployeeId && filterSelections.dateRange?.from) {
      fetchUserAssignments({
        year: filterSelections.dateRange?.from?.getFullYear(),
        quarter: filterSelections.dateRange?.from?.getMonth()
          ? Math.floor(filterSelections.dateRange.from.getMonth() / 3) + 1
          : undefined,
        userId: selectedEmployeeId,
      })
    }
  }, [selectedEmployeeId, filterSelections.dateRange, fetchUserAssignments])

  // Transform eligibleUsers to TreeSelector format
  const employeeFlatData: TreeSelectorChild[] = useMemo(() => {
    if (!eligibleUsers) return []

    return eligibleUsers.map((user) => ({
      id: user.id.toString(),
      label: user.fullName || user.username,
      disabled: false,
    }))
  }, [eligibleUsers])

  // Handle date picker changes
  const handleDateChange = useCallback(
    (newDate: DateRange | undefined, newDateFilterType: DateType) => {
      setFilterSelections((prev) => ({
        ...prev,
        dateRange: newDate,
        dateFilterType: newDateFilterType,
      }))
    },
    []
  )

  const handleEmployeeSelection = useCallback((employeeId: string) => {
    setSelectedEmployeeId(employeeId)
  }, [])

  // Transform assignments data for the table
  const assignmentsData: ScalrTableData[] = useMemo(
    () =>
      userAssignments && userAssignments.length > 0
        ? userAssignments.map((assignment) => ({
            userFullName: assignment.userFullName,
            kpi: `${assignment.kpiGroup} - ${assignment.kpiName} (${assignment.kpiUnit})`,
            branchName: assignment.branchName,
            period: `Q${assignment.quarter} ${assignment.year}`,
            targetValue: formatValueForKpi(
              assignment.targetValue,
              assignment.kpiUnit
            ),
            actualValue: formatValueForKpi(
              assignment.actualValue,
              assignment.kpiUnit
            ),
            bonusAmount: assignment.bonusAmount,
            achieved: (
              <ScalrBadge
                common
                conditionalStyle={() =>
                  assignment.achieved
                    ? semanticStyles.success
                    : semanticStyles.error
                }
              >
                {assignment.achieved ? 'Yes' : 'No'}
              </ScalrBadge>
            ) as any,
            progressPercent: assignment.progressPercent,
            createdAt: assignment.createdAt,
          }))
        : [],
    [userAssignments]
  )

  // Filter assignments based on selected employee and date range
  const filteredAssignmentsData = useMemo(() => {
    return assignmentsData.filter((assignment) => {
      // Filter by employee (required)
      if (selectedEmployeeId) {
        // Get the original assignment to access userId
        const originalAssignment = userAssignments?.find(
          (a) =>
            `${a.kpiGroup} - ${a.kpiName} (${a.kpiUnit})` === assignment.kpi &&
            a.branchName === assignment.branchName &&
            `Q${a.quarter} ${a.year}` === assignment.period
        )
        if (
          !originalAssignment ||
          originalAssignment.userId.toString() !== selectedEmployeeId
        ) {
          return false
        }
      }

      // Filter by date range (optional)
      if (filterSelections.dateRange?.from) {
        // Extract year and quarter from period string "Q1 2024"
        const periodMatch = assignment.period?.toString().match(/Q(\d+) (\d+)/)
        if (!periodMatch) return true

        const assignmentQuarter = parseInt(periodMatch[1])
        const assignmentYear = parseInt(periodMatch[2])

        const fromYear = filterSelections.dateRange.from.getFullYear()
        const fromQuarter =
          Math.floor(filterSelections.dateRange.from.getMonth() / 3) + 1

        if (filterSelections.dateFilterType === 'yearly') {
          if (assignmentYear !== fromYear) {
            return false
          }
        } else if (filterSelections.dateFilterType === 'quarterly') {
          if (
            assignmentYear !== fromYear ||
            assignmentQuarter !== fromQuarter
          ) {
            return false
          }
        }
      }

      return true
    })
  }, [assignmentsData, filterSelections, selectedEmployeeId, userAssignments])

  // Filter userAssignments for PayoutSummary (by employee and quarter)
  const filteredUserAssignmentsForPayout = useMemo(() => {
    if (
      !selectedEmployeeId ||
      !filterSelections.dateRange?.from ||
      !userAssignments
    ) {
      return []
    }

    const fromYear = filterSelections.dateRange.from.getFullYear()
    const fromQuarter =
      Math.floor(filterSelections.dateRange.from.getMonth() / 3) + 1

    return userAssignments.filter((assignment) => {
      // Filter by employee
      if (assignment.userId.toString() !== selectedEmployeeId) {
        return false
      }

      // Filter by quarter
      if (filterSelections.dateFilterType === 'yearly') {
        return assignment.year === fromYear
      } else if (filterSelections.dateFilterType === 'quarterly') {
        return (
          assignment.year === fromYear && assignment.quarter === fromQuarter
        )
      }

      return true
    })
  }, [userAssignments, selectedEmployeeId, filterSelections])

  return (
    <Card className="w-full min-h-screen">
      <CardHeader>
        <CardTitle pageTitle>{t('targets.navigation.kpiPayout')}</CardTitle>
      </CardHeader>
      <CardContent>
        <ScalrBreadcrumb
          breadcrumbItems={[
            { label: t('targets.navigation.targets'), href: '/targets' },
          ]}
          title={t('targets.navigation.kpiPayout')}
        />

        {/* Filter Section */}
        <div className="my-3">
          <Card>
            <CardContent className="p-3">
              {loading || eligibleUsersLoading ? (
                <Loading />
              ) : (
                <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
                  {/* Filter Controls */}
                  <div className="flex flex-col md:flex-row md:items-center space-y-4 md:space-y-0 md:space-x-4">
                    {/* Show employee selector when eligible users are loaded */}
                    {eligibleUsers && (
                      <TreeSelector
                        data={employeeFlatData}
                        selectedId={selectedEmployeeId}
                        onSelect={handleEmployeeSelection}
                        placeholder={t('common.labels.selectEmployee')}
                        loading={eligibleUsersLoading}
                        noTree
                        enableSearch
                      />
                    )}

                    <BasicDatePicker
                      value={filterSelections.dateRange}
                      dateFilterType={filterSelections.dateFilterType}
                      onChange={handleDateChange}
                      noMonths
                      yearsUnselectable
                    />
                  </div>

                  {/* Action Button */}
                  <div className="flex gap-4 justify-center md:justify-start items-center">
                    <Button
                      variant="outline"
                      size="sm"
                      disabled={!selectedEmployeeId}
                    >
                      <IconDownload className="mr-2 h-4 w-4" />
                      {t('common.buttons.export')}
                    </Button>
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Table Section - Show only when employee and date are selected */}
        {selectedEmployeeId && filterSelections.dateRange?.from ? (
          <>
            <div className="mt-3">
              <BasicTable
                data={filteredAssignmentsData}
                columns={getKpiPayoutColumns(t)}
                title={t('targets.navigation.kpiPayout')}
                loading={loading}
                enablePagination={true}
                itemsPerPage={6}
                showCard={true}
              />
            </div>

            {/* Payout Summary */}
            {filteredUserAssignmentsForPayout.length > 0 && (
              <PayoutSummary
                employeeId={selectedEmployeeId!}
                assignmentsData={filteredUserAssignmentsForPayout}
              />
            )}
          </>
        ) : (
          <div className="mt-3">
            <Card>
              <CardContent>
                {!selectedEmployeeId ? (
                  <ScalrEmptyBox
                    icon={IconUserOff as any}
                    title={t('targets.emptyStates.selectEmployeeTitle')}
                    message={t('targets.emptyStates.selectEmployeeMessage')}
                    size="md"
                  />
                ) : !filterSelections.dateRange?.from ? (
                  <ScalrEmptyBox
                    icon={IconFilter as any}
                    title={t('common.labels.selectQuarter')}
                    message={t('common.labels.selectQuarter')}
                    size="md"
                  />
                ) : null}
              </CardContent>
            </Card>
          </div>
        )}
      </CardContent>
    </Card>
  )
}

export default KPIPayoutPage
